import { useMemo, useState } from "react";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import MovieModal from "@/components/base/MovieModal";
import { categoryMovies } from "@/mocks/movies";
import { genreFilters, sortOptions } from "@/mocks/home";
import type { Movie } from "@/pages/home/types";

const tintMap: Record<string, string> = {
  primary: "bg-primary-50 border-primary-200",
  accent: "bg-accent-50 border-accent-200",
  secondary: "bg-secondary-50 border-secondary-200",
};

const chipMap: Record<string, string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-800",
  secondary: "bg-secondary-100 text-secondary-800",
};

export default function CategoryBrowser() {
  const [genre, setGenre] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [sort, setSort] = useState("rating");
  const [selected, setSelected] = useState<Movie | null>(null);

  const results = useMemo(() => {
    const list = categoryMovies.filter((movie) => {
      const matchGenre = genre === "all" || movie.genre === genre;
      const text = `${movie.title}${movie.genre}${movie.region}${movie.tags.join("")}${movie.blurb}`;
      const matchKeyword = keyword.trim() === "" || text.includes(keyword.trim());
      return matchGenre && matchKeyword;
    });

    return [...list].sort((a, b) => {
      if (sort === "year") return b.year - a.year;
      if (sort === "title") return a.title.localeCompare(b.title, "zh-Hans-CN");
      return b.rating - a.rating;
    });
  }, [genre, keyword, sort]);

  const reset = () => {
    setGenre("all");
    setKeyword("");
    setSort("rating");
  };

  return (
    <section id="browse" className="w-full bg-background-100 py-14 md:py-20">
      <div className="max-w-content mx-auto px-4 md:px-8">
        <Reveal>
          <SectionHeading
            eyebrow="分类浏览"
            title="按题材与关键词，找到当下想看的"
            desc="下方列表只作用于本页已有的条目，输入片名或选择题材后结果会即时刷新，排序方式同样可以随时切换。"
          />
        </Reveal>

        <Reveal delay={80}>
          <div className="mt-8 rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="输入片名、题材或关键词，如「悬疑」「上海」"
                  className="w-full pl-9 pr-3 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  aria-label="搜索本页影片"
                />
              </div>
              <div className="flex items-center gap-3">
                <label htmlFor="sort-select" className="text-sm text-foreground-600 whitespace-nowrap">
                  排序
                </label>
                <select
                  id="sort-select"
                  value={sort}
                  onChange={(event) => setSort(event.target.value)}
                  className="px-3 py-2.5 pr-8 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
                >
                  {sortOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {genreFilters.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setGenre(item.id)}
                  className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    genre === item.id
                      ? "bg-primary-500 text-background-50"
                      : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                  }`}
                  aria-pressed={genre === item.id}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="mt-6 flex items-center justify-between gap-4">
          <p className="text-sm text-foreground-600">
            当前筛选到 <strong className="text-primary-600">{results.length}</strong> 部作品
          </p>
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-restart-line"></i>
            重置条件
          </button>
        </div>

        {results.length === 0 ? (
          <div className="mt-6 rounded-lg bg-background-50 border border-dashed border-background-300 py-16 text-center">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-search-line text-2xl"></i>
            </span>
            <p className="mt-4 text-foreground-800 font-medium">没有找到符合条件的作品</p>
            <p className="mt-1 text-sm text-foreground-500">试试更换关键词，或点“重置条件”查看全部条目。</p>
            <button
              type="button"
              onClick={reset}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部作品
            </button>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((movie, index) => (
              <Reveal key={movie.id} delay={(index % 3) * 60}>
                <article
                  className={`h-full rounded-lg border p-5 flex flex-col ${tintMap[movie.tone]}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${chipMap[movie.tone]}`}>
                      {movie.genre}
                    </span>
                    <span className="inline-flex items-center gap-1 text-sm font-bold text-foreground-900">
                      <i className="ri-star-fill text-accent-500"></i>
                      {movie.rating.toFixed(1)}
                    </span>
                  </div>

                  <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">{movie.title}</h3>
                  <p className="mt-1 text-xs text-foreground-500">
                    {movie.year} · {movie.region} · {movie.duration} · {movie.quality}
                  </p>
                  <p className="mt-3 text-sm text-foreground-700 leading-relaxed line-clamp-3">{movie.blurb}</p>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {movie.tags.map((tag) => (
                      <span key={tag} className="text-xs text-foreground-500">
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => setSelected(movie as Movie)}
                    className="mt-4 pt-4 border-t border-foreground-900/10 inline-flex items-center gap-1.5 text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors cursor-pointer self-start"
                  >
                    查看详情
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <MovieModal
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected?.title ?? ""}
        quality={selected?.quality}
        blurb={selected?.blurb ?? ""}
        tags={selected?.tags ?? []}
        meta={[
          { label: "题材", value: selected?.genre ?? "-" },
          { label: "年份", value: selected ? String(selected.year) : "-" },
          { label: "地区", value: selected?.region ?? "-" },
          { label: "时长", value: selected?.duration ?? "-" },
        ]}
      />
    </section>
  );
}